import posthog, { type CaptureResult } from "posthog-js";
import { onAuthStateChanged, type Auth } from "firebase/auth";
import type { Router, RouteLocationNormalizedLoaded } from "vue-router";

type SignInMethod = "admin_password" | "operator_password" | "operator_code";

let enabled = false;
let authReady = false;

// Keep SDK identity/session and device metadata, but exclude automatically
// collected URLs, referrers, person properties and arbitrary application data.
const allowedProperties = new Set([
  "token",
  "distinct_id",
  "$device_id",
  "$user_id",
  "$anon_distinct_id",
  "$session_id",
  "$window_id",
  "$insert_id",
  "$time",
  "$lib",
  "$lib_version",
  "$browser",
  "$browser_version",
  "$os",
  "$os_version",
  "$device_type",
  "$screen_height",
  "$screen_width",
  "$viewport_height",
  "$viewport_width",
  "$is_identified",
  "$process_person_profile",
  "$geoip_disable",
  "app",
  "app_version",
  "environment",
  "route_name",
  "sign_in_method",
]);

export function getAnalyticsPage(route: RouteLocationNormalizedLoaded) {
  return {
    route_name: typeof route.name === "string" ? route.name : "unknown",
    // Vue's matched path is the route template, with no customer/job/invoice IDs.
    $pathname:
      route.matched[route.matched.length - 1]?.path.replace(/\?/g, "") ||
      "/unknown",
  };
}

export function sanitizeAnalyticsEvent(
  event: CaptureResult | null,
  page: ReturnType<typeof getAnalyticsPage>,
  origin: string,
): CaptureResult | null {
  if (
    !event ||
    !["$pageview", "$identify", "user_signed_in", "user_signed_out"].includes(
      event.event,
    )
  ) {
    return null;
  }

  const properties = Object.fromEntries(
    Object.entries(event.properties).filter(([key]) =>
      allowedProperties.has(key),
    ),
  );
  return {
    uuid: event.uuid,
    event: event.event,
    timestamp: event.timestamp,
    properties: {
      ...properties,
      ...page,
      $current_url: `${origin}${page.$pathname}`,
      $host: new URL(origin).host,
    },
  };
}

function safely(action: () => void) {
  // Analytics must never block navigation, authentication or job-sheet work.
  try {
    action();
  } catch {
    // SDK failures are deliberately non-fatal.
  }
}

export function trackSignIn(method: SignInMethod) {
  if (!enabled || !authReady) return;
  safely(() => posthog.capture("user_signed_in", { sign_in_method: method }));
}

export function initializeWebAnalytics(router: Router, auth: Auth) {
  if (enabled) return;
  const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim();
  const host = import.meta.env.VITE_POSTHOG_HOST?.trim();
  if (!token || !host || import.meta.env.VITE_POSTHOG_ENABLED === "false")
    return;
  if (import.meta.env.DEV && import.meta.env.VITE_POSTHOG_ENABLED !== "true")
    return;

  safely(() => {
    const appProperties = {
      app: "intransvoice_web",
      app_version: PACKAGE_VERSION,
      environment: import.meta.env.MODE,
    };
    posthog.init(token, {
      api_host: host,
      defaults: "2026-05-30",
      person_profiles: "identified_only",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      capture_exceptions: false,
      capture_performance: false,
      capture_heatmaps: false,
      rageclick: false,
      advanced_disable_flags: true,
      disable_external_dependency_loading: true,
      cross_subdomain_cookie: false,
      ip: false,
      before_send: (event) =>
        sanitizeAnalyticsEvent(
          event,
          getAnalyticsPage(router.currentRoute.value),
          window.location.origin,
        ),
    });
    posthog.register(appProperties);
    enabled = true;

    let pendingPageview = false;
    let previousUserId: string | null = null;
    const capturePageview = () => safely(() => posthog.capture("$pageview"));

    router.afterEach((_to, _from, failure) => {
      if (failure) return;
      if (authReady) capturePageview();
      else pendingPageview = true;
    });

    onAuthStateChanged(auth, (user) => {
      safely(() => {
        const userId = user ? `intransvoice:${user.uid}` : null;
        const storedUserId = posthog.get_property("$user_id");
        if (storedUserId && storedUserId !== userId) {
          if (previousUserId && !userId) posthog.capture("user_signed_out");
          posthog.reset(true);
          // reset() also clears super properties.
          posthog.register(appProperties);
        }
        if (userId && userId !== previousUserId) posthog.identify(userId);
        previousUserId = userId;
        authReady = true;
        if (pendingPageview) {
          pendingPageview = false;
          capturePageview();
        }
      });
    });
  });
}
